import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, ShieldCheck, MessageCircle, Building2, Phone, Mail } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import { typeLabel, localitySlug } from "../lib/constants";
import { useSettings } from "../hooks/useSettings";
import PropertyCard from "../components/PropertyCard";
import { EnquiryForm } from "../components/EnquiryForm";
import Seo from "../components/Seo";

const HERO = "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob21lJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzg3MzA0MTExfDA&ixlib=rb-4.1.0&q=85";

const Home = () => {
  const { data: settings } = useSettings();
  const [facets, setFacets] = useState({ localities: [], property_types: [], cities: [] });
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/properties", { params: { featured: true, limit: 6 } })
      .then((r) => setFeatured(r.data.items)).catch(() => {});
  }, []);

  const wa = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
  const waHref = `https://wa.me/${wa}`;
  const topLocalities = facets.localities.slice(0, 5);

  return (
    <div>
      <Seo
        title="Satyam Real Estate — Property in Jaipur | Buy, Rent Flats, Villas, Plots"
        description="Find flats, villas, plots, commercial spaces and agricultural land across Jaipur with Satyam Real Estate. Explore areas, browse verified listings and connect on WhatsApp."
        image={HERO}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          name: settings?.firm_name || "Satyam Real Estate",
          areaServed: "Jaipur, Rajasthan, India",
          telephone: settings?.phone,
          email: settings?.email,
          address: settings?.address,
        }}
      />

      {/* Hero */}
      <section className="relative min-h-[86vh] flex items-center py-20">
        <img src={HERO} alt="Luxury home in Jaipur" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/90 via-stone-950/65 to-stone-950/35" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block text-brand-light text-xs font-bold uppercase tracking-[0.3em] mb-5">
              Jaipur • The Pink City
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.1]">
              Buy, rent or sell property in Jaipur.
            </h1>
            <p className="mt-6 text-lg text-stone-200 max-w-xl">
              From apartments and villas to plots, farmland and commercial spaces — tell us your requirement and our team will guide you to the right property in the right area.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="hero-whatsapp"
                className="flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-sm font-semibold hover:opacity-90 transition-opacity">
                <MessageCircle size={18} /> WhatsApp Us
              </a>
              <a href={`tel:${settings?.phone}`} data-testid="hero-call"
                className="flex items-center gap-2 rounded-full bg-white/10 text-white px-5 py-3 text-sm font-semibold border border-white/20 hover:bg-white/20 transition-colors">
                <Phone size={18} /> {settings?.phone}
              </a>
            </div>
          </motion.div>

          {/* Lead capture — property search will be reintroduced later */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="bg-white rounded-2xl p-6 sm:p-7 shadow-2xl w-full max-w-md lg:ml-auto" data-testid="hero-lead">
            <h2 className="font-heading text-2xl text-stone-900 mb-1">Get a free call back</h2>
            <p className="text-sm text-stone-500 mb-5">Tell us what you need — buy, rent or sell. We'll reach out shortly.</p>
            <EnquiryForm showIntent defaultIntent="buy" />
          </motion.div>
        </div>
      </section>

      {/* Explore by locality */}
      {topLocalities.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand">Explore Jaipur</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-2">Popular localities</h2>
            </div>
            <Link to="/properties" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-stone-700 hover:text-brand">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid gap-5 md:grid-cols-4 md:grid-rows-2 md:h-[520px]">
            {topLocalities.map((loc, i) => (
              <Link key={loc.name} to={`/localities/${localitySlug(loc.name, loc.city)}`}
                data-testid={`locality-card-${i}`}
                className={`group relative rounded-2xl overflow-hidden ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`}>
                <img src={imageUrl(loc.image)} alt={loc.name} className="w-full h-full min-h-[200px] object-cover img-zoom" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <p className="flex items-center gap-1.5 text-white/80 text-xs mb-1"><MapPin size={13} /> Jaipur</p>
                  <h3 className="font-heading text-2xl text-white">{loc.name}</h3>
                  <p className="text-sm text-white/80">{loc.count} propert{loc.count > 1 ? "ies" : "y"}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Property types */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand">What we offer</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-2">Browse by property type</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {facets.property_types.map((t) => (
              <Link key={t.name} to={`/properties?property_type=${t.name}`} data-testid={`type-card-${t.name}`}
                className="group rounded-2xl border border-stone-200 p-6 text-center hover:border-brand hover:bg-accent transition-colors">
                <Building2 className="mx-auto text-brand mb-3" size={28} />
                <p className="font-semibold text-stone-800 text-sm">{typeLabel(t.name)}</p>
                <p className="text-xs text-stone-500 mt-1">{t.count} listings</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand">Handpicked</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-stone-900 mt-2">Featured properties</h2>
            </div>
            <Link to="/properties" className="flex items-center gap-1.5 text-sm font-semibold text-stone-700 hover:text-brand">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p) => <PropertyCard key={p._id} p={p} />)}
          </div>
        </section>
      )}

      {/* Why us + CTA */}
      <section className="bg-stone-950 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-brand-light">Why Satyam Real Estate</span>
            <h2 className="font-heading text-3xl sm:text-4xl mt-3 mb-8">Local expertise you can trust</h2>
            <ul className="space-y-6">
              {[
                { icon: MapPin, t: "Area-first guidance", d: "We tell you which localities suit your budget and needs before you visit." },
                { icon: ShieldCheck, t: "Verified listings", d: "Every property is checked so you deal only with genuine options." },
                { icon: MessageCircle, t: "Direct, no-hassle contact", d: "Reach us instantly on WhatsApp, call or email — no lengthy forms." },
              ].map((f) => (
                <li key={f.t} className="flex gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand/20 text-brand-light shrink-0"><f.icon size={20} /></span>
                  <div>
                    <p className="font-semibold">{f.t}</p>
                    <p className="text-sm text-stone-400 mt-1">{f.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 backdrop-blur">
            <h3 className="font-heading text-2xl mb-3">Looking for something specific?</h3>
            <p className="text-stone-300 text-sm mb-7">Tell us your requirement and budget — or just reach us directly right now:</p>
            <div className="space-y-3">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="cta-whatsapp"
                className="flex items-center gap-4 rounded-2xl bg-[#25D366] text-white px-5 py-4 hover:opacity-90 transition-opacity">
                <MessageCircle size={22} />
                <span><span className="block text-xs text-white/80">WhatsApp</span><span className="block font-semibold">+{wa}</span></span>
              </a>
              <a href={`tel:${settings?.phone}`} data-testid="cta-call"
                className="flex items-center gap-4 rounded-2xl bg-white/10 border border-white/15 px-5 py-4 hover:bg-white/15 transition-colors">
                <Phone size={22} className="text-brand-light" />
                <span><span className="block text-xs text-stone-400">Call us</span><span className="block font-semibold">{settings?.phone}</span></span>
              </a>
              <a href={`mailto:${settings?.email}`} data-testid="cta-email"
                className="flex items-center gap-4 rounded-2xl bg-white/10 border border-white/15 px-5 py-4 hover:bg-white/15 transition-colors">
                <Mail size={22} className="text-brand-light" />
                <span><span className="block text-xs text-stone-400">Email</span><span className="block font-semibold break-all">{settings?.email}</span></span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
